/**
 * API client for the PIM dashboard Hono backend.
 * All requests go through the Worker proxy which injects the active store's shop parameter.
 */

const BASE = '/api';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((body as { error?: string }).error ?? `Request failed: ${res.status}`);
  }

  // Guard against non-JSON responses (e.g., HTML from SPA fallback)
  const ct = res.headers.get('content-type') ?? '';
  if (!ct.includes('application/json')) {
    throw new Error(`Expected JSON but got ${ct || 'unknown'} from ${path}`);
  }

  return res.json() as Promise<T>;
}

// ── Stores ────────────────────────────────────────────
export const fetchStores = () =>
  request<{ stores: import('../types').Store[] }>('/stores');

/** Build query string, stripping empty values (e.g. shop='' for All Stores) */
function qs(params: Record<string, string>): string {
  const filtered = Object.entries(params).filter(([_, v]) => v !== '');
  return filtered.length ? '?' + new URLSearchParams(filtered).toString() : '';
}

// ── Dashboard ─────────────────────────────────────────
export const fetchOverview = (shop: string, days = '30') =>
  request<import('../types').DashboardOverview>(`/dashboard/overview${qs({ shop, days })}`);

// ── Products ──────────────────────────────────────────
export const fetchProducts = (shop: string, params?: Record<string, string>) => {
  return request<import('../types').PaginatedProducts>(`/products${qs({ shop, ...params })}`);
};

export const fetchUnoptimized = (shop: string) =>
  request<{ products: import('../types').Product[] }>(`/unoptimized${qs({ shop })}`);

export const fetchProductDiff = (shop: string, productId: string) =>
  request<import('../types').ProductDiff>(`/products/${productId}/diff${qs({ shop })}`);

export const fetchProductFacets = (shop: string) =>
  request<import('../types').ProductFacets>(`/products/facets${qs({ shop })}`);

// ── Actions ───────────────────────────────────────────
export const optimizeProduct = (shop: string, productId: string, force = false) =>
  request<{ success: boolean }>('/products/optimize', {
    method: 'POST',
    body: JSON.stringify({ shop, productId, force }),
  });

export const bulkOptimize = (shop: string, options?: { limit?: number; force?: boolean; reset_originals?: boolean }) =>
  request<{ jobId: string }>('/products/bulk-optimize', {
    method: 'POST',
    body: JSON.stringify({ shop, ...options }),
  });

export const resetProduct = (shop: string, productId: string, mode: 'clear' | 'set' | 'revert', fields?: Record<string, string>) =>
  request<{ success: boolean }>('/products/reset', {
    method: 'POST',
    body: JSON.stringify({ shop, productId, mode, fields }),
  });

export const retryError = (shop: string, jobId: string) =>
  request<{ success: boolean }>('/products/retry', {
    method: 'POST',
    body: JSON.stringify({ shop, jobId }),
  });

export const resolveError = (shop: string, errorId: string) =>
  request<{ success: boolean }>('/products/resolve-error', {
    method: 'POST',
    body: JSON.stringify({ shop, errorId }),
  });

// ── Jobs ──────────────────────────────────────────────
export const fetchJobs = (shop: string) =>
  request<{ jobs: import('../types').Job[] }>(`/jobs${qs({ shop })}`);

export const fetchJob = (shop: string, jobId: string) =>
  request<import('../types').Job>(`/jobs/${jobId}${qs({ shop })}`);

// ── Vendors ───────────────────────────────────────────
export const fetchVendors = (shop: string) =>
  request<{ vendors: import('../types').VendorSummary[] }>(`/vendors${qs({ shop })}`);

// ── Errors ────────────────────────────────────────────
export const fetchErrors = (shop: string) =>
  request<{ errors: import('../types').ProductError[] }>(`/errors${qs({ shop })}`);

// ── Rules ──────────────────────────────────────────────
export const fetchRules = (params?: Record<string, string>) =>
  request<{ rules: import('../types').ProductRule[]; count: number }>(`/rules${qs(params || {})}`);

export const fetchRule = (id: string) =>
  request<import('../types').ProductRule>(`/rules/${id}`);

export const createRule = (body: Partial<import('../types').ProductRule>) =>
  request<{ id: string; created: boolean }>('/rules', { method: 'POST', body: JSON.stringify(body) });

export const updateRule = (id: string, body: Partial<import('../types').ProductRule>) =>
  request<{ id: string; updated: boolean }>(`/rules/${id}`, { method: 'PUT', body: JSON.stringify(body) });

export const deleteRule = (id: string) =>
  request<{ id: string; deleted: boolean }>(`/rules/${id}`, { method: 'DELETE' });

// ── Notifications ──────────────────────────────────────
export const fetchChannels = () =>
  request<{ channels: import('../types').NotificationChannel[] }>('/notifications/channels');

export const createChannel = (body: Partial<import('../types').NotificationChannel>) =>
  request<{ id: string; created: boolean }>('/notifications/channels', { method: 'POST', body: JSON.stringify(body) });

export const updateChannel = (id: string, body: Partial<import('../types').NotificationChannel>) =>
  request<{ id: string; updated: boolean }>(`/notifications/channels/${id}`, { method: 'PUT', body: JSON.stringify(body) });

export const deleteChannel = (id: string) =>
  request<{ id: string; deleted: boolean }>(`/notifications/channels/${id}`, { method: 'DELETE' });

export const testChannel = (id: string) =>
  request<{ success: boolean; error?: string }>(`/notifications/channels/${id}/test`, { method: 'POST' });

export const fetchSubscriptions = () =>
  request<{ subscriptions: import('../types').NotificationSubscription[] }>('/notifications/subscriptions');

export const saveSubscription = (body: Partial<import('../types').NotificationSubscription>) =>
  request<{ id: string; created: boolean }>('/notifications/subscriptions', { method: 'POST', body: JSON.stringify(body) });

export const deleteSubscription = (id: string) =>
  request<{ id: string; deleted: boolean }>(`/notifications/subscriptions/${id}`, { method: 'DELETE' });

export const fetchNotificationLog = (limit = 100) =>
  request<{ log: import('../types').NotificationLogEntry[] }>(`/notifications/log${qs({ limit: String(limit) })}`);

export const fetchEventTypes = () =>
  request<{ event_types: import('../types').EventType[] }>('/notifications/events');
